import AbstractGroupItem from '../item/abstractGroupItem';
import AbstractValidator from './abstractValidator';
import ValidationResult from './validationResult';
import CombinedGroupItem from '../item/combinedGroupItem';

/**
 * @class CollisionValidator
 * @extends AbstractValidator
 */
export default class CollisionValidator extends AbstractValidator {
    /**
     * @param {ProblemFactory} problemFactory
     * @param {ItemCollisionChecker} collisionChecker
     * @param {string} message
     * @param {string} problemType
     */
    constructor(problemFactory, collisionChecker, message) {
        super(problemFactory);

        this.collisionChecker = collisionChecker;
        this.message = message;
    }

    /**
     * @param {AbstractItem} item
     * @return {ValidationResult[]}
     */
    validateItem(item) {
        if (!item.hasParentItem()) return [new ValidationResult(this)];

        if (item instanceof AbstractGroupItem) {
            const items = item.resolveGroupItems();
            return [new ValidationResult(this, items, this._validateItems(items))];
        }

        return [new ValidationResult(this, [item], this._validateSingleItem(item))];
    }

    /**
     * Validates a single non-group item
     *
     * @param {AbstractItem} item
     * @return {AbstractProblem[]}
     * @private
     */
    _validateSingleItem(item) {
        if (!this._isItemSupported(item)) return [];

        const otherItems = this._findOtherItems(item);

        return this
            .collisionChecker
            .check(item, otherItems.filter(child => (child !== item && this._isItemSupported(child))))
            .map(collision => this._createProblemForCollision(collision));
    }

    /**
     * Validates all items in a group, recursive
     *
     * @param {AbstractItem[]} items
     * @return {AbstractProblem[]}
     * @protected
     */
    _validateItems(items) {
        return items
            .map(child => this._validateSingleItem(child))
            .reduce((flattened, problems) => flattened.concat(problems), []);
    }

    /**
     * Find other items to compare the given item to
     *
     * @param {AbstractItem} item
     * @return {AbstractItem[]}
     * @private
     */
    _findOtherItems(item) {
        const items = item
            .getParentItem()
            .getItems()
            .map(child => (child instanceof AbstractGroupItem ? child.resolveGroupItems() : [child]))
            .reduce((children, child) => children.concat(child), []);

        return this._filterCombinedGroupItemSiblings(item, items);
    }

    _filterCombinedGroupItemSiblings(item, items) {
        const rootCombinedGroup = item
            .getAncestorGroups()
            .concat([item])
            .find(group => group instanceof CombinedGroupItem);

        if (!rootCombinedGroup) return items;

        return items.filter(child => rootCombinedGroup.resolveGroupItems().indexOf(child) < 0);
    }

    /**
     * @param {AbstractCollision} collision
     * @return {AbstractProblem}
     * @private
     */
    _createProblemForCollision(collision) {
        return this
            .problemFactory
            .createCollisionError(
                'collision',
                this.message,
                'warning-sign',
                [collision.item, collision.otherItem],
                collision,
            );
    }

    _isItemSupported(item) {
        return item.supportsCollisions && item.hasParentItem();
    }
}
